import { createFileRoute } from '@tanstack/react-router'
import { DashboardPage } from '@/features/dashboard/DashboardPage'

/**
 * Dashboard search params. `project_id` is an optional override that features a
 * specific project in the Next Best Action card (instead of the most-recent
 * one) — the seam for previewing the featured card across projects/agents.
 */
interface DashboardSearch {
  project_id?: string
  /** Set by the invite-lane post-accept redirect — surfaces the I4 landed
   *  banner. Mirrored on the root schema so the banner reads it via inherited
   *  search; declared here so `navigate({ to: '/dashboard', search })` from
   *  InviteSignupForm doesn't get the fields stripped by strict validation. */
  joined?: string
  role?: 'admin' | 'member'
  /** Set by the create-workspace success redirect — pops the A3 welcome
   *  starter-kit modal on first landing. Cleared on dismiss. */
  welcome?: '1'
}

/**
 * The home dashboard — a full-page authenticated view (the Shell renders it with
 * `hideWorkspace`, see `_app.tsx`). Replaces `/dashboard-mock` as the real,
 * API-wired home.
 */
export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardPage,
  validateSearch: (search: Record<string, unknown>): DashboardSearch => {
    const roleRaw = typeof search.role === 'string' ? search.role : undefined
    const role =
      roleRaw === 'admin' || roleRaw === 'member' ? roleRaw : undefined
    return {
      project_id: typeof search.project_id === 'string' ? search.project_id : undefined,
      joined: typeof search.joined === 'string' ? search.joined : undefined,
      role,
      // Tolerate string `'1'` (navigate-time) AND number `1` (URL round-trip
      // via TSR's default `JSON.parse`-based search parser) — mirrors the root
      // validator so the signal survives `WorkspaceSearchSync`'s href-mirror.
      welcome:
        search.welcome === '1' || search.welcome === 1 ? '1' : undefined,
    }
  },
})
